import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation } from "@angular/core";
import { ModalComponent } from "../modal.component";
import { OrganizationService } from "../../../services/organization.service";
import { StateService } from "../../../services/state.service";
import { S25Util } from "../../../util/s25-util";
import { Item } from "../../../pojo/Item";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { AddressI } from "../../../pojo/AddressI";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { Bind } from "../../../decorators/bind.decorator";
import { Proto } from "../../../pojo/Proto";
import CustAttrCode = CustomAttributes.CustAttrCode;
import NumericalBoolean = Proto.NumericalBoolean;

type OrganizationCreationView = "form" | "success";

interface OrganizationCreationCustomAttr {
    custAttrId: number;
    itemId: number;
    itemLabel: string;
    itemName: unknown;
    itemTypeId: CustAttrCode;
    multi_val?: NumericalBoolean;
    isNew: boolean;
    data?: unknown;
}

@Component({
    selector: "s25-ng-organization-creation-modal",
    template: `
        <s25-modal-header [data]="params()"></s25-modal-header>
        <div class="modal-body">
            @if (view() === "form") {
                <div class="create-org-wrapper">
                    <div class="organization-creation-field organization-creation-field--custom-attrs">
                        @for (attr of customAttrs(); track attr.custAttrId) {
                            <div class="ng-editable-custom-attr-row">
                                <button
                                    type="button"
                                    class="c-textButton ng-cust-attr-remove"
                                    [attr.aria-label]="'Remove Custom Attribute ' + attr.itemLabel"
                                    (click)="removeCustomAttr(attr)"
                                >
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>
                                <s25-ng-editable-custom-attribute
                                    [attributeId]="attr.custAttrId"
                                    [attributeType]="attr.itemTypeId"
                                    [attributeName]="attr.itemLabel"
                                    [value]="attr.data"
                                    [multiVal]="attr.multi_val"
                                    [readOnly]="false"
                                    [hasCommit]="true"
                                    [onCommit]="commitCustomAttr(attr)"
                                    [itemTypeId]="Item.Ids.Organization"
                                    [hasUploadFile]="true"
                                    [resolveCustomAttributeLabel]="resolveCustomAttributeLabel"
                                />
                                @if (attr.isNew) {
                                    <div class="ngFinePrint ngRed">Edit this custom attribute to add it.</div>
                                }
                            </div>
                        }
                        @if (showCustomAttrDropdown()) {
                            <s25-custom-attr-dropdown
                                [itemTypeId]="Item.Ids.Organization"
                                [filter]="filterCustomAttrs"
                                [useSecurity]="true"
                                (valueChange)="addCustomAttr($event)"
                            />
                        } @else {
                            <button
                                type="button"
                                class="aw-button aw-button--primary c-margin-top--quarter"
                                (click)="showCustomAttrDropdown.set(true)"
                            >
                                Add a Custom Attribute
                            </button>
                        }
                    </div>

                    <div class="organization-creation-field">
                        <label class="organization-creation-label" for="organization-creation-name">
                            Organization Name
                            <span class="ngBold ngRed">*</span>
                        </label>
                        <input id="organization-creation-name" type="text" class="c-input" [(ngModel)]="name" />
                    </div>

                    <div class="organization-creation-field">
                        <label class="organization-creation-label" for="organization-creation-title">
                            Organization Title
                        </label>
                        <input id="organization-creation-title" type="text" class="c-input" [(ngModel)]="title" />
                    </div>

                    <div class="organization-creation-field">
                        <label class="organization-creation-label" for="organization-creation-type">
                            Organization Type
                        </label>
                        <s25-ng-dropdown-search-criteria
                            id="organization-creation-type"
                            class="c-input"
                            [placeholder]="'Select Organization Type'"
                            [type]="'organizationTypes'"
                            [value]="orgType()"
                            (valueChange)="orgType.set($event)"
                        />
                    </div>

                    <div class="organization-creation-field organization-creation-address">
                        <s25-ng-address [(model)]="addressModel" [readOnly]="false" [hasPhone]="true" />
                    </div>
                </div>
            } @else {
                <div class="success-msg">
                    <div>
                        <s25-ng-icon [type]="'check'" [ariaLabel]="false" size="3em" />
                        Organization Successfully Created
                    </div>
                    <div class="modal-footer success-footer">
                        <button type="button" class="aw-button aw-button--outline" (click)="goToRecord()">
                            View Organization Details
                        </button>
                        <button
                            type="button"
                            class="aw-button aw-button--outline c-margin-left--single"
                            (click)="params()?.closeModal()"
                        >
                            Close
                        </button>
                    </div>
                </div>
            }
        </div>
        @if (view() === "form") {
            <s25-ng-modal-footer [content]="footerTemplate" [bordered]="true" />
            <ng-template #footerTemplate>
                <div class="footer-container">
                    @if (saving()) {
                        <s25-ng-loading-inline-static />
                    }
                    @if (errorMessage()) {
                        <div class="ngBold ngRed">{{ errorMessage() }}</div>
                    }
                    <button type="button" class="aw-button aw-button--outline" (click)="params()?.closeModal()">
                        Cancel
                    </button>
                    <button type="button" class="aw-button aw-button--primary" [disabled]="saving()" (click)="save()">
                        Create
                    </button>
                </div>
            </ng-template>
        }
    `,
    styles: `
        .create-org-wrapper {
            width: 85%;
            margin: auto;
            display: flex;
            flex-direction: column;
            gap: 1.25em;
        }

        .organization-creation-label {
            display: block;
            margin-bottom: 0.35em;
        }

        .organization-creation-field > .c-input,
        .organization-creation-field > s25-ng-dropdown-search-criteria {
            display: block;
            width: 100%;
        }

        .organization-creation-field--custom-attrs ::ng-deep .editable-controls input.s25-input {
            font-size: 16px;
            height: 2em !important;
            min-height: 2em !important;
        }

        .organization-creation-field--custom-attrs ::ng-deep .editable-buttons {
            display: none;
        }

        .organization-creation-address ::ng-deep .editable-address > div {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: flex-start;
            max-width: none;
            width: 100%;
            margin-bottom: 1.25em;
            gap: 0.35em;
        }

        .organization-creation-address ::ng-deep .editable-address label {
            display: block;
        }

        .organization-creation-address ::ng-deep .editable-address .c-input,
        .organization-creation-address ::ng-deep .editable-address .street {
            display: block;
            width: 100%;
            min-width: unset;
            margin: 0;
        }

        .success-msg {
            height: 20em;
        }

        .success-msg > div:first-child {
            width: fit-content;
            margin: 0 auto;
            padding-top: 5em;
            font-size: 1.25em;
            display: flex;
            align-items: center;
            gap: 1em;
        }

        .success-msg s25-ng-icon {
            background-color: var(--color--objects-event);
            border-radius: 50%;
            color: var(--color--surface-bg);
            padding: 7px;
        }

        .success-msg .modal-footer.success-footer {
            position: absolute;
            bottom: 5px;
            right: 0;
            width: 100%;
        }

        .footer-container {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5em;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class OrganizationCreationModalComponent extends ModalComponent {
    static readonly size = "md" as const;
    static readonly title = "Organization Creation";
    static readonly asDialog = true;

    readonly Item = Item;
    readonly view = signal<OrganizationCreationView>("form");
    readonly customAttrs = signal<OrganizationCreationCustomAttr[]>([]);
    readonly showCustomAttrDropdown = signal(false);
    readonly orgType = signal<DropDownItem | undefined>(undefined);
    readonly saving = signal(false);
    readonly errorMessage = signal("");
    readonly newOrgId = signal<number | undefined>(undefined);

    name = "";
    title = "";
    addressModel: Partial<AddressI> = {};

    @Bind
    filterCustomAttrs(item: DropDownItem) {
        const itemVal = item.val;
        if (itemVal == null) return false;
        const custAttrId = +itemVal;
        return (
            !CustomAttributeService.isReadOnly(custAttrId) &&
            !this.customAttrs().some((attr) => attr.custAttrId === custAttrId)
        );
    }

    @Bind
    resolveCustomAttributeLabel(custAttrId: number) {
        return this.customAttrs().find((attr) => attr.custAttrId === custAttrId)?.itemLabel;
    }

    commitCustomAttr(attr: OrganizationCreationCustomAttr) {
        return (value: unknown) => this.onCustomAttrCommit(attr, value);
    }

    addCustomAttr(chosen: DropDownItem) {
        if (!chosen?.custAtrbType || chosen.itemId == null) return;
        const { itemId, itemName, txt, custAtrbType, multi_val } = chosen;
        const custAttrId = +itemId;
        this.customAttrs.update((attrs) => [
            ...attrs,
            {
                custAttrId,
                itemId: custAttrId,
                itemLabel: itemName ?? txt ?? "",
                itemName: null,
                itemTypeId: custAtrbType.toUpperCase() as CustAttrCode,
                multi_val: multi_val as NumericalBoolean | undefined,
                isNew: true,
            },
        ]);
        this.showCustomAttrDropdown.set(false);
    }

    removeCustomAttr(attr: OrganizationCreationCustomAttr) {
        this.customAttrs.update((attrs) => attrs.filter((item) => item.custAttrId !== attr.custAttrId));
    }

    onCustomAttrCommit(attr: OrganizationCreationCustomAttr, value: unknown) {
        this.customAttrs.update((attrs) =>
            attrs.map((item) => (item.custAttrId === attr.custAttrId ? { ...item, data: value, isNew: false } : item)),
        );
    }

    async save() {
        this.errorMessage.set("");
        if (!this.name?.trim()) {
            this.errorMessage.set("Organization name is required");
            return;
        }

        this.saving.set(true);
        try {
            const orgTypeId = this.orgType()?.itemId ?? this.orgType()?.val;
            const response = await OrganizationService.createOrganization(
                this.name,
                this.title,
                orgTypeId != null ? +orgTypeId : undefined,
                this.addressModel,
                { item: this.customAttrs() },
            );
            this.newOrgId.set(response?.results?.info?.id);
            this.view.set("success");
        } catch (caughtError) {
            this.errorMessage.set(S25Util.errorText(caughtError, "Failed to create organization"));
        } finally {
            this.saving.set(false);
        }
    }

    goToRecord() {
        const orgId = this.newOrgId();
        if (!orgId) return;
        return StateService.gotoItem(
            {
                itemTypeId: Item.Ids.Organization,
                itemId: orgId,
                forceView: "details",
            },
            true,
        );
    }
}
